import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Mail, Trash2, Crown, Shield } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api';
const getToken = () => localStorage.getItem('accessToken') || localStorage.getItem('token');

interface TeamMember {
  id: string;
  _id?: string;
  name: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  joinedAt?: string;
}

interface TeamTabProps {
  userPlan: string;
  userEmail: string;
}

export default function TeamTab({ userPlan, userEmail }: TeamTabProps) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const isEnterprise = userPlan === 'enterprise';
  const maxTeamMembers = isEnterprise ? Infinity : 5;
  const isTeamFull = teamMembers.length >= maxTeamMembers;

  const fetchTeamMembers = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const res = await fetch(`${API_URL}/team/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setTeamMembers(data.members || data.data || []);
      } else if (data.members) {
        setTeamMembers(data.members);
      }
    } catch (err) {
      console.error('Failed to fetch team:', err);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const handleInvite = async () => {
    setError('');
    setSuccessMsg('');

    if (!inviteEmail || !inviteEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    if (isTeamFull) {
      setError(`Team is full (max ${maxTeamMembers} members). Upgrade to Enterprise for unlimited.`);
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/team/invite`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole })
      });
      const data = await res.json();

      if (data.success) {
        setSuccessMsg(`Invitation sent to ${inviteEmail}`);
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('viewer');
        fetchTeamMembers();
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        setError(data.error || data.message || 'Failed to send invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!window.confirm(`Remove ${memberName} from the team?`)) return;

    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/team/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.success) {
        setSuccessMsg(`${memberName} removed from team`);
        fetchTeamMembers();
        setTimeout(() => setSuccessMsg(''), 3000);
      } else {
        setError(data.error || 'Failed to remove member');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleChangeRole = async (memberId: string, newRole: 'admin' | 'editor' | 'viewer') => {
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/team/members/${memberId}/role`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });
      const data = await res.json();

      if (data.success) {
        fetchTeamMembers();
        setSuccessMsg('Role updated');
        setTimeout(() => setSuccessMsg(''), 2000);
      }
    } catch (err) {
      console.error('Failed to update role:', err);
    }
  };

  const roleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return { bg: '#ede9fe', color: '#7c3aed', icon: Crown };
      case 'editor': return { bg: '#dbeafe', color: '#2563eb', icon: Shield };
      case 'viewer': return { bg: '#f3f4f6', color: '#6b7280', icon: Users };
      default: return { bg: '#f3f4f6', color: '#6b7280', icon: Users };
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', margin: 0 }}>
          <Users size={20} />
          Team Members
          <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 'normal' }}>
            ({teamMembers.length}/{isEnterprise ? '∞' : maxTeamMembers})
          </span>
        </h3>
        <button
          onClick={() => setShowInviteModal(true)}
          disabled={isTeamFull}
          style={{
            padding: '10px 20px',
            background: isTeamFull ? '#d1d5db' : '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: isTeamFull ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            transition: 'all 0.15s'
          }}
        >
          <UserPlus size={18} />
          {isTeamFull ? 'Team Full' : 'Invite Member'}
        </button>
      </div>

      {/* Success/Error Messages */}
      {successMsg && (
        <div style={{ padding: '12px 16px', background: '#ecfdf5', color: '#065f46', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', border: '1px solid #a7f3d0' }}>
          ✅ {successMsg}
        </div>
      )}
      {error && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', color: '#991b1b', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', border: '1px solid #fecaca' }}>
          ❌ {error}
          <button onClick={() => setError('')} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: 'bold' }}>×</button>
        </div>
      )}

      {/* Team List */}
      {teamMembers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <Users size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ fontSize: '15px', marginBottom: '16px' }}>No team members yet</p>
          <button
            onClick={() => setShowInviteModal(true)}
            style={{
              padding: '10px 24px',
              background: '#7c3aed',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            <UserPlus size={16} style={{ marginRight: '6px' }} />
            Invite Your First Team Member
          </button>
        </div>
      ) : (
        <div style={{ background: '#f9fafb', borderRadius: '12px', overflow: 'hidden' }}>
          {teamMembers.map((member) => {
            const roleStyle = roleBadgeColor(member.role);
            const RoleIcon = roleStyle.icon;
            const isYou = member.email === userEmail;

            return (
              <div
                key={member.id || member._id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px',
                  borderBottom: '1px solid #e5e7eb',
                  background: 'white',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}
              >
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong style={{ fontSize: '14px' }}>{member.name || 'Unknown'}</strong>
                    {isYou && (
                      <span style={{ background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>
                        You
                      </span>
                    )}
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '13px', marginTop: '2px' }}>{member.email}</div>
                  {member.joinedAt && (
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                      Joined {new Date(member.joinedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {/* Role Selector */}
                  {!isYou && isEnterprise ? (
                    <select
                      value={member.role}
                      onChange={(e) => handleChangeRole(member.id || member._id || '', e.target.value as any)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '12px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  ) : (
                    <span style={{
                      background: roleStyle.bg,
                      color: roleStyle.color,
                      padding: '4px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      textTransform: 'capitalize'
                    }}>
                      <RoleIcon size={12} />
                      {member.role}
                    </span>
                  )}

                  {/* Remove Button */}
                  {!isYou && (
                    <button
                      onClick={() => handleRemoveMember(member.id || member._id || '', member.name || member.email)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ef4444',
                        cursor: 'pointer',
                        padding: '6px',
                        borderRadius: '6px',
                        transition: 'background 0.15s'
                      }}
                      title={`Remove ${member.name}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Plan Info */}
      {!isEnterprise && (
        <div style={{
          marginTop: '16px',
          padding: '14px 18px',
          background: '#fef3c7',
          borderRadius: '10px',
          border: '1px solid #fcd34d',
          fontSize: '13px',
          color: '#92400e',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Crown size={16} />
          <span>
            <strong>{teamMembers.length}/{maxTeamMembers}</strong> members used.
            Upgrade to <strong>Enterprise</strong> for unlimited team members.
          </span>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div
          onClick={() => setShowInviteModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)',
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              padding: '28px',
              borderRadius: '14px',
              width: '440px',
              maxWidth: '90vw',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                <Mail size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Invite Team Member
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '4px 8px',
                  borderRadius: '6px'
                }}
              >
                ×
              </button>
            </div>

            {/* Email Input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                Email Address
              </label>
              <input
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1.5px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>

            {/* Role Select */}
            <div style={{ marginBottom: '22px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                Role
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as any)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1.5px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="admin">Admin — Full access</option>
                <option value="editor">Editor — Can edit campaigns</option>
                <option value="viewer">Viewer — View only</option>
              </select>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={loading || !inviteEmail}
                style={{
                  padding: '10px 24px',
                  background: loading || !inviteEmail ? '#9ca3af' : '#7c3aed',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loading || !inviteEmail ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Mail size={16} />
                {loading ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}